import FastAPIInstance from '../../services/risksystem3-public-api';
import Risksystem3PublicAuthService from '../../services/risksystem3-public-auth-service';
import { captureReportedError } from '../sentry';

export enum ErrorType {
  GENERAL_ERROR = 'GENERAL_ERROR',
  PDF_GENERATION_ERROR = 'PDF_GENERATION_ERROR',
  API_ERROR = 'API_ERROR',
  COMPONENT_ERROR = 'COMPONENT_ERROR',
  REACT_ERROR_BOUNDARY = 'REACT_ERROR_BOUNDARY',
  DATA_PROCESSING_ERROR = 'DATA_PROCESSING_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  PERMISSION_ERROR = 'PERMISSION_ERROR',
}

export interface ErrorDetails {
  message: string;
  url: string;
  stack: string;
  componentStack: string | null;
  userAgent: string;
  timestamp: string;
  userName: string;
  userId: string;
  errorType: ErrorType;
  context?: Record<string, any>;
  count?: number; // Add count for debounced errors
}

// Debounce configuration
const DEBOUNCE_WINDOW_MS = 2000; // 2 seconds
const MAX_DEBOUNCE_COUNT = 100; // Max count to prevent memory issues

interface PendingError {
  message: string;
  error: any;
  errorType: ErrorType;
  context?: Record<string, any>;
  count: number;
  firstOccurrence: number;
  lastOccurrence: number;
  timeoutId?: NodeJS.Timeout;
}

// Map to store pending errors by their unique key
const pendingErrors = new Map<string, PendingError>();

/**
 * Creates a unique key for an error based on its characteristics
 */
const createErrorKey = (
  message: string,
  error: any,
  errorType: ErrorType,
  context?: Record<string, any>,
): string => {
  const errorMessage = error?.message || error?.toString() || 'Unknown error';
  const stack = error?.stack || '';
  const contextString = context ? JSON.stringify(context) : '';

  // Create a hash-like key from the error characteristics
  return `${errorType}:${message}:${errorMessage}:${stack.slice(0, 200)}:${contextString}`;
};

/**
 * Sends a debounced error report
 */
const sendDebouncedError = (
  errorKey: string,
  pendingError: PendingError,
): void => {
  const { message, error, errorType, context, count, firstOccurrence } =
    pendingError;

  const isProduction = process.env.NODE_ENV === 'production';
  const debounceContext = {
    ...context,
    _debounced: true,
    _count: count,
    _firstOccurrence: new Date(firstOccurrence).toISOString(),
    _lastOccurrence: new Date(pendingError.lastOccurrence).toISOString(),
  };

  if (isProduction) {
    reportErrorAsync(message, error, errorType, debounceContext);
    captureReportedError(message, error, errorType, debounceContext);
  } else {
    const displayCount =
      count >= MAX_DEBOUNCE_COUNT ? `${MAX_DEBOUNCE_COUNT}+` : count.toString();
    console.error(
      `[${errorType}] ${message} (${displayCount} occurrences):`,
      error,
    );
    if (context && Object.keys(context).length > 0) {
      console.error('Context:', context);
    }
  }

  // Remove from pending errors
  pendingErrors.delete(errorKey);
};

/**
 * Error reporting function that sends errors to the API endpoint
 * for email notifications in production, or logs to console in development.
 *
 * The function handles the async API call internally and falls back to console.error
 * if the reporting fails. You don't need to await it or handle promises.
 *
 * @param message - Human readable error message
 * @param error - The error object or any error-like value
 * @param errorType - Category for the error (use ErrorType enum)
 * @param context - Optional additional context data to include with the error
 */
export const reportError = (
  message: string,
  error: any,
  errorType: ErrorType,
  context?: Record<string, any>,
): void => {
  const errorKey = createErrorKey(message, error, errorType, context);
  const now = Date.now();

  // Check if we already have a pending error for this key
  const existingError = pendingErrors.get(errorKey);

  if (existingError) {
    // Update existing error count and reset timeout
    existingError.count = Math.min(existingError.count + 1, MAX_DEBOUNCE_COUNT);
    existingError.lastOccurrence = now;

    // Clear existing timeout
    if (existingError.timeoutId) {
      clearTimeout(existingError.timeoutId);
    }

    // Set new timeout
    existingError.timeoutId = setTimeout(() => {
      sendDebouncedError(errorKey, existingError);
    }, DEBOUNCE_WINDOW_MS);

    return;
  }

  // Create new pending error
  const pendingError: PendingError = {
    message,
    error,
    errorType,
    context,
    count: 1,
    firstOccurrence: now,
    lastOccurrence: now,
  };

  // Set timeout to send the error
  pendingError.timeoutId = setTimeout(() => {
    sendDebouncedError(errorKey, pendingError);
  }, DEBOUNCE_WINDOW_MS);

  // Store the pending error
  pendingErrors.set(errorKey, pendingError);
};

/**
 * Internal async function that actually sends the error to the API
 * Only called in production
 */
const reportErrorAsync = async (
  message: string,
  error: any,
  errorType: ErrorType,
  context?: Record<string, any>,
): Promise<void> => {
  try {
    const user = Risksystem3PublicAuthService.getCurrentUser();
    const currentUrl = window.location.href;

    // Create enhanced message with count if it's a debounced error
    let enhancedMessage = message;
    if (context?._debounced && context?._count > 1) {
      const displayCount =
        context._count >= MAX_DEBOUNCE_COUNT
          ? `${MAX_DEBOUNCE_COUNT}+`
          : context._count.toString();
      enhancedMessage = `${message} (${displayCount} occurrences)`;
    }

    const errorDetails: ErrorDetails = {
      message: `${enhancedMessage}: ${error?.message || error?.toString() || 'Unknown error'}`,
      url: currentUrl,
      stack: error?.stack || 'No stack trace available',
      componentStack: null,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      userName: user?.user_name || 'Unknown',
      userId: user?.user_id || 'Unknown',
      errorType: errorType,
      context: context || {},
      count: context?._count || 1,
    };

    // Send error to API endpoint for email notification
    await FastAPIInstance.post('v1/errors/send_console_error', errorDetails);
  } catch (reportingError) {
    // Fallback to console if error reporting fails
    console.warn('Failed to send error log:', reportingError);
    console.error(message, error);
  }
};

/**
 * Force flush all pending errors (useful for cleanup or testing)
 */
export const flushPendingErrors = (): void => {
  for (const [errorKey, pendingError] of pendingErrors.entries()) {
    if (pendingError.timeoutId) {
      clearTimeout(pendingError.timeoutId);
    }
    sendDebouncedError(errorKey, pendingError);
  }
};

/**
 * Clear all pending errors without sending them (useful for cleanup)
 */
export const clearPendingErrors = (): void => {
  for (const pendingError of pendingErrors.values()) {
    if (pendingError.timeoutId) {
      clearTimeout(pendingError.timeoutId);
    }
  }
  pendingErrors.clear();
};
